import {
  Alignment,
  Button,
  Menu,
  MenuItem,
  Navbar,
  NavbarDivider,
  NavbarGroup,
  Popover,
} from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { Fragment, useEffect, useMemo } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { REACT_APP_VIEW_DOMAIN } from "../../config/constants"
import { FlexContainer } from "../../styled/flexbox.styled"
import { SCIENT_ROUTES } from "../Routes"
import { ScientLogo } from "../components"
import { QuickChart } from "../components/QuickChart/QuickChart"
import { useAuth, useWindowDimensions } from "../hooks"
import { useGlobalState } from "../hooks/useGlobalState"
import { toaster } from "../utils/createToaster"
import { CustomMenuItem, IdeasDotCounter, NavContainer } from "./Nav.styled"
import NavButton from "./NavButton"
import { Icon } from "@blueprintjs/core"

const Nav = () => {
  const { pathname } = useLocation()
  const { ideasStats } = useGlobalState()
  const { logout, profile } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (profile && !profile.has_sapi_identity) {
      toaster.show({
        message: (
          <div>
            <Icon icon="warning-sign" />
            You are not connected to Bloomberg. The application will use end-of-day prices instead
            of live market prices.
            <br />
            <small>Click on Account → Connect to SAPI to enable live market prices.</small>
          </div>
        ),
        intent: "warning",
        timeout: 5000,
      })
    }
  }, [profile])

  /**
   * Get ideas stats in the context in order to display unseen idea counter in the nav bar
   */
  const unseenIdeas = useMemo(
    () => ideasStats?.unseen_ideas_count,
    [ideasStats?.unseen_ideas_count],
  )
  const unseenCount = useMemo(
    () => (!unseenIdeas ? "" : unseenIdeas < 100 ? unseenIdeas.toString() : "99+"),
    [unseenIdeas],
  )

  /**
   * Retrive screen's width to set how many navButton are visible
   */
  const { width } = useWindowDimensions()

  /**
   * A tab either routes inside the app (`route`) or opens an absolute URL in a
   * new browser tab (`href`), the way a published model page does.
   */
  const navButtonList: {
    icon: (typeof IconNames)[keyof typeof IconNames]
    text: string
    route?: string
    href?: string
    withIdeasDotCounter?: boolean
  }[] = [
    { icon: IconNames.TH_LIST, route: SCIENT_ROUTES.FUNDAMENTALS, text: "Fundamentals" },
    {
      icon: IconNames.LIGHTBULB,
      route: SCIENT_ROUTES.IDEAS,
      text: "Ideas",
      withIdeasDotCounter: true,
    },
    {
      icon: IconNames.CHART,
      route: SCIENT_ROUTES.PORTFOLIO_MANAGEMENT,
      text: "Portfolio Management",
    },
    { icon: IconNames.COMPARISON, route: SCIENT_ROUTES.RECONCILIATION, text: "Reconciliation" },
    // The Trade Recon site is a static page on the view CDN, not an app
    // route. The trailing slash is load-bearing: CloudFront only rewrites
    // `/recon/` to the page; `/recon` answers 403. Hidden where the CDN
    // origin is unset (development issues no signed cookies).
    ...(REACT_APP_VIEW_DOMAIN
      ? [
          {
            icon: IconNames.EXCHANGE,
            href: `${REACT_APP_VIEW_DOMAIN.replace(/\/$/, "")}/recon/`,
            text: "Trade Recon",
          },
        ]
      : []),
    { icon: IconNames.WRENCH, route: SCIENT_ROUTES.CONFIG, text: "Config" },
    { icon: IconNames.HELP, route: SCIENT_ROUTES.ADDIN, text: "Addin" },
    { icon: IconNames.SCATTER_PLOT, route: SCIENT_ROUTES.DATAVIZ, text: "Dataviz" },
    // Only add Warren tab if user is in warren group
    ...(profile?.groups.includes("warren")
      ? [{ icon: IconNames.CHAT, route: SCIENT_ROUTES.WARREN, text: "Warren" }]
      : []),
    // Last on purpose: Warren is conditional, so appending after it keeps
    // Models at the end of the bar for everyone.
    { icon: IconNames.TH, route: SCIENT_ROUTES.MODELS, text: "Models" },
  ]

  // Determine how many buttons should be visible
  // Progressive breakpoints to show more icons while reserving space for Account button
  const numberOfvisibleButtons =
    width > 1700
      ? navButtonList.length
      : width > 1500
        ? 11
        : width > 1350
          ? 10
          : width > 1200
            ? 9
            : width > 1100
              ? 8
              : width > 970
                ? 7
                : width > 768
                  ? 5
                  : width > 630
                    ? 3
                    : 0

  const visibleNavButtons = navButtonList.slice(0, numberOfvisibleButtons)
  const hiddenNavButtons = navButtonList.slice(numberOfvisibleButtons)

  return (
    <NavContainer>
      <Navbar fixedToTop>
        <NavbarGroup align={Alignment.LEFT}>
          <Link to={{ pathname: SCIENT_ROUTES.HOME }}>
            <ScientLogo
              color="white"
              style={{ height: "28px", opacity: "0.3", marginRight: "4px" }}
            />
          </Link>
          {profile?.groups.includes("admin") && <QuickChart />}
          <NavbarDivider />
          {visibleNavButtons.map((button, index) => (
            <Fragment key={index}>
              <NavButton
                icon={button.icon}
                to={{ pathname: button.route ?? "" }}
                href={button.href}
                text={button.text}
              />
              {button.withIdeasDotCounter && unseenCount && (
                <IdeasDotCounter>{unseenCount}</IdeasDotCounter>
              )}
            </Fragment>
          ))}
          {hiddenNavButtons.length > 0 && (
            <Popover
              content={
                <Menu>
                  {hiddenNavButtons.map((button, index) => (
                    <FlexContainer key={index}>
                      <CustomMenuItem
                        icon={button.icon}
                        text={button.text}
                        href={button.href ?? button.route}
                        target={button.href ? "_blank" : undefined}
                        rel={button.href ? "noreferrer" : undefined}
                        active={button.route ? pathname.startsWith(button.route) : false}
                      />
                      {button.withIdeasDotCounter && unseenCount && (
                        <IdeasDotCounter isInMenuItem={true}>{unseenCount}</IdeasDotCounter>
                      )}
                    </FlexContainer>
                  ))}
                </Menu>
              }
            >
              <Button icon={IconNames.DOUBLE_CHEVRON_RIGHT} minimal />
            </Popover>
          )}
        </NavbarGroup>

        <NavbarGroup align={Alignment.RIGHT}>
          <Popover
            interactionKind="hover"
            content={
              <Menu>
                <MenuItem disabled text={`Connected as ${profile?.username}`} />
                <MenuItem
                  icon={IconNames.CELL_TOWER}
                  color={profile?.has_sapi_identity ? "success" : "danger"}
                  text={`${profile?.has_sapi_identity ? "SAPI: Connected" : "SAPI: Not connected"}`}
                  onClick={() => navigate(SCIENT_ROUTES.BLOOMBERG_AUTH)}
                />
                <MenuItem icon="log-out" onClick={() => logout && logout()} text="Log out" />
              </Menu>
            }
            placement="bottom-start"
          >
            <Button icon={IconNames.USER} text="Account" minimal />
          </Popover>
        </NavbarGroup>
      </Navbar>
    </NavContainer>
  )
}

export default Nav
